import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Actions
import { getRegionNames } from "../../../actions/regions/RegionActions";
import {
  getZones,
  clearZones,
  getSubZones,
  clearSubZones,
  createSubZones,
  updateSubZones,
  deleteSubZones,
  openCreateAndUpdateSubZonesForm,
} from "../../actions/campaign-planning/ZonesAndSubZonesActions";

// Constants
import { FormDataTargets } from "../../../constants/GeneralConstants";

// Components
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import Spinner from "../../../components/spinner/Spinner";
import { PlainDropdown } from "../../../components/dropdown/Dropdown";
import AlertMessage from "../../../mavin/components/alert-message/AlertMessage";
import SingleCitySelectionForm from "../../../mavin/components/single-city-selection-form/SingleCitySelectionForm";
import { LoadingData } from "../../../pages/org/OrgUtils";
import CityConfigSidebar from "./CityConfigSidebar";
import { CitySelectSection } from "./CityConfigUtils";
import CreateAndUpdateSubZonesForm from "./CreateAndUpdateSubZonesForm";

// Page Components
function SubZonesListSection({ selectedZoneId }) {
  // Freq Opt Locations
  const subZones = useSelector((state) => state.zonesAndSubZones.subZones);
  const subZonesLoading = useSelector(
    (state) => state.zonesAndSubZones.getSubZonesLoading
  );

  // if zone not selected
  if (!selectedZoneId) {
    return null;
  }

  // sub zones loading
  if (subZonesLoading) {
    return <LoadingData className="p-1 mt-3" dataType={"Sub Zones"} />;
  }

  // const subZoneNames
  const subZonesInfo = subZones?.[0] || {};
  const { subZoneNames = [] } = subZonesInfo || {};

  // if subZones not present
  const isSubZonesPresent = subZoneNames.length > 0;

  return (
    <div className="mt-4">
      <h5>Sub Zones</h5>

      {/* no data */}
      {!isSubZonesPresent && (
        <p className="font-italic text-muted">No Sub Zones Present</p>
      )}

      {/* Sub Zones */}
      <ul className="row">
        {isSubZonesPresent &&
          subZoneNames.map((subZone, i) => (
            <li key={i} className="col-3 pl-0">
              {subZone}
            </li>
          ))}
      </ul>
    </div>
  );
}

// create and update SubZones Button
function CreateAndUpdateSubZonesButton({ selectedCityId, selectedZoneId }) {
  const dispatch = useDispatch();

  // Sub Zones
  const subZones = useSelector((state) => state.zonesAndSubZones.subZones);
  const subZonesLoading = useSelector(
    (state) => state.zonesAndSubZones.getSubZonesLoading
  );

  // Selector
  const createSubZonesLoading = useSelector(
    (state) => state.zonesAndSubZones.createSubZonesLoading
  );
  const updateSubZonesLoading = useSelector(
    (state) => state.zonesAndSubZones.updateSubZonesLoading
  );
  const deleteSubZonesLoading = useSelector(
    (state) => state.zonesAndSubZones.deleteSubZonesLoading
  );
  const createUpdateDeleteLoading =
    createSubZonesLoading || updateSubZonesLoading || deleteSubZonesLoading;

  // hide button conditions
  if (!selectedCityId || !selectedZoneId || subZonesLoading) {
    return null;
  }

  // checking subZones present or not
  // based of this key, we will figure out we have to create or update subZones
  const isUpdateSubZones = subZones.length > 0;
  const buttonLabel = isUpdateSubZones ? "Update" : "Create";

  return (
    <button
      className="btn btn-primary shadow-none"
      disabled={createUpdateDeleteLoading}
      data-toggle="modal"
      data-target={`#${FormDataTargets.createAndUpdateSubZonesForm}`}
      onClick={() =>
        dispatch(openCreateAndUpdateSubZonesForm(isUpdateSubZones))
      }
    >
      {buttonLabel} Sub Zones{" "}
      {createUpdateDeleteLoading && <Spinner className="spinner-border-sm" />}
    </button>
  );
}

function SelectZoneSection({
  selectedCityId,
  selectedZoneId,
  setSelectedZoneId,
  setSelectedZoneName,
}) {
  const dispatch = useDispatch();

  // Zones
  const zones = useSelector((state) => state.zonesAndSubZones.zones);
  const zonesLoading = useSelector(
    (state) => state.zonesAndSubZones.zonesLoading
  );

  const isZonesPresent = zones.length > 0;

  // hide button if no city selected
  if (!selectedCityId) {
    return null;
  }

  // zones loading alert
  if (zonesLoading) {
    return (
      <AlertMessage
        isLoading={true}
        textMessage="Zones Loading..."
        className="m-0 p-1 px-5 ml-5"
        spinnerClassName="spinner-border-sm"
      />
    );
  }

  // dropdown constants
  const zonesConstants = zones.map((zone) => {
    const { id, name } = zone;
    return { id, label: name };
  });

  // function
  function handleSelectZone(zoneId) {
    const selectedZone = zonesConstants.find((zone) => zone.id === zoneId);
    const { label: zoneName } = selectedZone || {};

    // update state
    setSelectedZoneId(zoneId);
    setSelectedZoneName(zoneName);

    // get subZones
    dispatch(clearSubZones());
    dispatch(getSubZones(selectedCityId, zoneId));
  }

  return (
    <div className="ml-5 row align-items-center">
      <h5 className="mb-0 mr-2">Selected Zone : </h5>
      {!isZonesPresent && (
        <span className="font-italic text-muted">No Zone Present</span>
      )}
      {isZonesPresent && (
        <PlainDropdown
          items={zonesConstants}
          className="btn shadow-none rounded-lg border px-3"
          placeHolder="Select"
          selectedId={selectedZoneId}
          onItemSelect={handleSelectZone}
          dropMenuClassName="dropdown-fixed-height"
          isFirstItemSelectable={true}
        />
      )}
    </div>
  );
}

/**
 * SubZones city config Page
 */
export default function SubZonesCityConfigPage() {
  const dispatch = useDispatch();

  // State
  const [selectedCityId, setSelectedCityId] = useState("");
  const [selectedZoneId, setSelectedZoneId] = useState("");
  const [selectedZoneName, setSelectedZoneName] = useState("");

  // regions name list
  const regionsName = useSelector((state) => state.region.regionsName) || [];
  const regionNamesLoading = useSelector((state) => state.region.namesLoading);

  // single City-Selection-form
  const openSingleCitySelectionForm = useSelector(
    (state) => state.singleCitySelectionModal.openModal
  );

  // create update subZones
  const openCreateAndUpdateSubZonesModal = useSelector(
    (state) => state.zonesAndSubZones.openCreateAndUpdateSubZonesModal
  );

  useEffect(() => {
    dispatch(getRegionNames());
  }, [dispatch]);

  // Functions
  function onSubmitCitySelectionForm(cityId) {
    setSelectedCityId(cityId);

    // toast
    toast.info("Select Zone");

    // clear selected state
    setSelectedZoneId("");
    setSelectedZoneName("");

    // get zones and subZones
    dispatch(clearSubZones());
    dispatch(clearZones());
    dispatch(getZones(cityId));
  }

  function onSubmitCreateUpdateSubZonesForm(
    isUpdate,
    subZonesCSV,
    subzoneListId
  ) {
    // subZonesBean
    const subZonesBean = {
      zoneId: selectedZoneId,
      zoneName: selectedZoneName,
      subZonesCSV: subZonesCSV,
    };

    // delete subZones if subZones are empty
    if (isUpdate && !subZonesCSV) {
      dispatch(deleteSubZones(selectedCityId, selectedZoneId, subzoneListId));
      return;
    }

    // update and create
    if (isUpdate) {
      dispatch(updateSubZones(selectedCityId, subzoneListId, subZonesBean));
    } else {
      dispatch(createSubZones(selectedCityId, subZonesBean));
    }
  }

  return (
    <>
      <CityConfigSidebar activePageUrl={"/city-config/sub-zones"} />

      <div className="content-wrapper">
        <PageHeader title={"Sub Zones"} shadow={true} />

        <div className="page-content">
          {/* regions list loading */}
          {regionNamesLoading && <LoadingData className="p-1" />}
          {!regionNamesLoading && (
            <>
              <div className="d-flex align-items-center justify-content-between border-bottom pb-3">
                <div className="d-flex align-items-center">
                  <CitySelectSection
                    regionsName={regionsName}
                    selectedCityId={selectedCityId}
                  />
                  <SelectZoneSection
                    selectedCityId={selectedCityId}
                    selectedZoneId={selectedZoneId}
                    setSelectedZoneId={setSelectedZoneId}
                    setSelectedZoneName={setSelectedZoneName}
                  />
                </div>

                <CreateAndUpdateSubZonesButton
                  selectedCityId={selectedCityId}
                  selectedZoneId={selectedZoneId}
                />
              </div>

              {/* Sub Zones List */}
              <SubZonesListSection selectedZoneId={selectedZoneId} />
            </>
          )}
        </div>
      </div>

      {/* MODALS */}
      {openSingleCitySelectionForm && (
        <SingleCitySelectionForm
          regionsName={regionsName}
          onSubmit={onSubmitCitySelectionForm}
          selectedCityId={selectedCityId}
        />
      )}

      {openCreateAndUpdateSubZonesModal && (
        <CreateAndUpdateSubZonesForm
          zoneName={selectedZoneName}
          onSubmit={onSubmitCreateUpdateSubZonesForm}
        />
      )}
    </>
  );
}
